import React, { useEffect, useState } from "react";
import {
  Card,
  FormControlLabel,
  Grid,
  Icon,
  Radio,
  Tooltip,
  TextField,
  MenuItem,
  RadioGroup,
  Select,
  FormControl,
} from "@mui/material";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import MDBox from "components/MDBox";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import DataTable from "examples/Tables/DataTable";
import MDBadge from "components/MDBadge";
import CenterPopup from "components/CommonComponent/RightPanelDrawer";
import { STATUS } from "components/common/constant";
import RequiredLabel from "components/CommonComponent/RequiredLabel";
import { showAlert } from "components/CommonComponent/ShowAlert";
import {
  selectSx,
  radioLabelSx,
} from "components/CommonComponent/CommonFunction";
import { ArrowDropDown } from "@mui/icons-material";
import {
  addContractor,
  getContractorList,
  getContractorById,
  updateContractorById,
  deleteContractorById,
} from "actions/contractor";
import { useDispatch, useSelector } from "react-redux";
import { useNotify } from "components/CommonComponent/NotificationProvider";
import {
  DEFAULT_RECORDS_PER_PAGE,
  hasAccess,
} from "components/common/constant";
import { contractorList } from "constants/contractor";

const initialFields = {
  id: null,
  name: {
    value: "",
    original: "",
    error: "",
  },
  status: {
    value: "ACTIVE",
    original: "ACTIVE",
    error: "",
  },
};

function ContractorListing() {
  const dispatch = useDispatch();
  const { notify } = useNotify();
  const contractorListData = useSelector(
    (state) => state.contractor.contractorList,
  );

  const authData = JSON.parse(localStorage.getItem("p")) || {};
  const currentRole = authData?.role;

  const canCreate = hasAccess("CONTRACTOR_MASTER", "CREATE")?.includes(
    currentRole,
  );
  const canUpdate = hasAccess("CONTRACTOR_MASTER", "UPDATE")?.includes(
    currentRole,
  );
  const canDelete = hasAccess("CONTRACTOR_MASTER", "DELETE")?.includes(
    currentRole,
  );

  const canShowActions = canUpdate || canDelete;
  const [pageNum, setPageNum] = useState(1);
  const [panelMode, setPanelMode] = useState(null); //add /edit
  const [formData, setFormData] = useState(initialFields);
  const [listState, setListState] = useState({
    isLoading: true,
    error: "",
    noRecords: false,
    showLoaderOnClick: false,
  });
  const [filters, setFilters] = useState({
    name: "",
    status: "",
    filterParams: [],
  });

  useEffect(() => {
    getContractorList(dispatch, pageNum, filters.filterParams);
    return () => {
      dispatch({ type: contractorList, payload: {} });
    };
  }, [dispatch, pageNum, filters.filterParams]);

  useEffect(() => {
    if (contractorListData?.code === 200) {
      setListState({
        isLoading: false,
        error: "",
        noRecords: contractorListData?.data?.Contractor?.length === 0,
        showLoaderOnClick: false,
      });
    } else if (contractorListData?.code || contractorListData?.message) {
      setListState({
        isLoading: false,
        error:
          contractorListData?.message ||
          "Failed to load contractor list! Please try again.",
        noRecords: false,
        showLoaderOnClick: false,
      });
    }
  }, [contractorListData]);

  const tableData =
    contractorListData?.data?.Contractor?.map((item, index) => ({
      ...item,
      id: item?.ID,
      srNo:
        (pageNum - 1) *
          (contractorListData?.data?.noOfRecordsPerPage ||
            DEFAULT_RECORDS_PER_PAGE) +
        index +
        1,
      name: item?.name || "-",
      status: item?.status || "-",
    })) || [];

  const handleChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: { value, error: "" },
    }));
  };

  const handleClear = () => {
    setFilters({
      name: "",
      status: null,
      filterParams: [],
    });
    if (pageNum !== 1) setPageNum(1);
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    try {
      setListState((prev) => ({
        ...prev,
        showLoaderOnClick: true,
      }));

      let payload = {};

      Object.keys(formData).forEach((key) => {
        if (key === "id") return;

        const field = formData[key];

        if (panelMode !== "edit" || field.value !== field.original) {
          payload[key] = field.value;
        }
      });

      if (panelMode === "edit" && !Object.keys(payload).length) {
        notify("No changes detected", "info");
        setListState((prev) => ({
          ...prev,
          showLoaderOnClick: false,
        }));
        return;
      }

      const res =
        panelMode === "edit"
          ? await updateContractorById(formData.id, payload)
          : await addContractor(payload);

      if (res?.code === 200) {
        notify(
          res?.message ||
            (panelMode === "edit"
              ? "Contractor updated successfully"
              : "Contractor added successfully"),
          "success",
        );

        closeModal();
        getContractorList(dispatch, pageNum, filters.filterParams);
      } else {
        const message = Array.isArray(res?.message)
          ? res.message
              .map((err) => (err.Msg ? err.Msg : JSON.stringify(err)))
              .join(", ")
          : res?.message || "Failed to save contractor";

        notify(message, "error");
      }
    } catch (error) {
      notify(error?.message || "Something went wrong", "error");
    } finally {
      setListState((prev) => ({
        ...prev,
        showLoaderOnClick: false,
      }));
    }
  };

  const closeModal = () => {
    setPanelMode(null);
    setFormData({
      ...initialFields,
    });
  };

  const getValidationError = (name, value) => {
    const trimmedValue = typeof value === "string" ? value.trim() : value;
    switch (name) {
      case "name":
        return !trimmedValue ? "Please enter contractor name" : "";

      default:
        return "";
    }
  };

  //on blur to get if any validation error exits
  const handleOnBlur = (name, value) => {
    const trimmedValue = typeof value === "string" ? value.trim() : value;
    const error = getValidationError(name, trimmedValue);
    setFormData((prev) => ({
      ...prev,
      [name]: {
        ...prev[name],
        value: trimmedValue,
        error,
      },
    }));
  };

  const validateForm = () => {
    let newData = { ...formData };
    Object.keys(newData).forEach((key) => {
      if (key === "id") return;
      newData[key] = {
        ...newData[key],
        error: getValidationError(key, newData[key].value),
      };
    });
    setFormData(newData);
    return Object.keys(newData)
      .filter((key) => key !== "id")
      .every((key) => newData[key].error === "");
  };

  const openAddModal = () => {
    // e.stopPropagation();
    setPanelMode("add");
    setFormData(initialFields);
  };

  const buildFilterParams = () => {
    const filterParams = [];

    if (filters.name?.trim()) {
      filterParams.push({
        field: "Name",
        condition: "like",
        filterValues: [filters.name.trim()],
      });
    }

    if (filters.status) {
      filterParams.push({
        field: "Status",
        condition: "eq",
        filterValues: [filters.status],
      });
    }

    return filterParams.length > 0 ? JSON.stringify(filterParams) : [];
  };

  const handleSearch = () => {
    const newFilterParams = buildFilterParams();

    if (
      JSON.stringify(filters.filterParams) !== JSON.stringify(newFilterParams)
    ) {
      setListState((prev) => ({
        ...prev,
        isLoading: true,
        showLoaderOnClick: true,
      }));

      setFilters((prev) => ({
        ...prev,
        filterParams: newFilterParams,
      }));

      if (pageNum !== 1) setPageNum(1);
    }
  };

  const openEditModal = async (row) => {
    setPanelMode("edit");

    setListState((prev) => ({
      ...prev,
      isLoading: true,
    }));

    const res = await getContractorById(row.id);

    if (res?.code === 200) {
      const cont = res?.data;

      setFormData({
        id: cont?.ID,
        name: {
          value: cont?.name || "",
          original: cont?.name || "",
          error: "",
        },
        code: {
          value: cont?.code || "",
          original: cont?.code || "",
          error: "",
        },
        status: {
          value: cont?.status || "ACTIVE",
          original: cont?.status || "ACTIVE",
          error: "",
        },
      });
    } else {
      const message = Array.isArray(res?.message)
        ? res?.message
            .map((err) => (err.Msg ? err.Msg : JSON.stringify(err)))
            .join(", ")
        : res?.message || "Failed to fetch contractor details";
      notify(message, "error");
    }

    setListState((prev) => ({
      ...prev,
      isLoading: false,
    }));
  };

  const handleDelete = (row) => {
    showAlert({
      title: "Are you sure you want to delete this contractor?",
      message: `Contractor "${row.name}" will be deleted permanently.`,
      type: "warning",
      showCancel: true,
      confirmText: "Yes, delete",
      cancelText: "Cancel",

      onConfirm: async () => {
        try {
          setListState((prev) => ({
            ...prev,
            isLoading: true,
          }));

          const res = await deleteContractorById(row.id);

          if (res?.code === 200) {
            notify(
              res?.message || "Contractor deleted successfully",
              "success",
            );

            // refresh list
            getContractorList(dispatch, pageNum, filters.filterParams);
          } else {
            notify(res?.message || "Failed to delete contractor", "error");
          }
        } catch (error) {
          notify(error?.message || "Something went wrong", "error");
        } finally {
          setListState((prev) => ({
            ...prev,
            isLoading: false,
          }));
        }
      },

      onCancel: () => {},
    });
  };

  const renderStatusBadge = (status) => {
    if (!status) return null;
    return (
      <MDBadge
        badgeContent={status}
        color={status === "ACTIVE" ? "success" : "secondary"}
        variant="gradient"
        size="sm"
        circular
      />
    );
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSearch();
    }
  };

  const columns = [
    {
      Header: "Sr No.",
      accessor: "ID",
      align: "left",
    },
    {
      Header: "Name",
      accessor: "name",
      align: "left",
    },
    {
      Header: "Status",
      accessor: "status",
      align: "center",
      Cell: (cell) => renderStatusBadge(cell.row.original.status),
    },
    ...(canShowActions
      ? [
          {
            Header: "Action",
            accessor: "actions",
            align: "center",
            disableSortBy: true,
            Cell: (cell) => {
              const rowData = cell.row.original;

              return (
                <MDBox
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  gap={1}
                >
                  {canUpdate && (
                    <Tooltip title="Edit" arrow>
                      <Icon
                        sx={({ palette }) => ({
                          cursor: "pointer",
                          color: palette.secondary.main,
                        })}
                        onClick={() => openEditModal(rowData)}
                      >
                        <i className="fas fa-edit fa-sm" />
                      </Icon>
                    </Tooltip>
                  )}

                  {canUpdate && canDelete && (
                    <MDTypography variant="caption" mx={0.5}>
                      |
                    </MDTypography>
                  )}

                  {canDelete && (
                    <Tooltip title="Delete" arrow>
                      <Icon
                        color="error"
                        sx={{
                          cursor: "pointer",
                          opacity: 0.5,
                          "&:hover": { opacity: 1 },
                        }}
                        onClick={() => handleDelete(rowData)}
                      >
                        <i className="fas fa-trash" />
                      </Icon>
                    </Tooltip>
                  )}
                </MDBox>
              );
            },
          },
        ]
      : []),
  ];

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <MDBox width="100%">
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card sx={{ width: "100%" }}>
              <MDBox
                px={3}
                pt={3}
                pb={2}
                display="flex"
                justifyContent="space-between"
                alignItems="center"
              >
                <MDTypography variant="h5" fontWeight="bold">
                  Contractors
                </MDTypography>

                {canCreate && (
                  <MDButton
                    type="button"
                    variant="contained"
                    color="deepBlue"
                    sx={{
                      py: 1.2,
                      px: 3,
                      fontWeight: "bold",
                      fontSize: "0.9rem",
                      textTransform: "none",
                    }}
                    onClick={openAddModal}
                  >
                    Add Contractor
                  </MDButton>
                )}
              </MDBox>
              {/* Filters */}
              <MDBox px={3} pb={2} onKeyDown={handleKeyDown}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} sm={6} md={4} lg={2}>
                    <TextField
                      fullWidth
                      placeholder="Search by name"
                      size="small"
                      value={filters.name}
                      onChange={(e) =>
                        setFilters({ ...filters, name: e.target.value })
                      }
                    />
                  </Grid>

                  <Grid item xs={12} sm={6} md={4} lg={2}>
                    <FormControl fullWidth size="small" sx={selectSx("100%")}>
                      <Select
                        value={filters.status || ""}
                        displayEmpty
                        onChange={(e) =>
                          setFilters({ ...filters, status: e.target.value })
                        }
                        IconComponent={ArrowDropDown}
                        renderValue={(selected) => {
                          if (!selected)
                            return (
                              <span style={{ color: "rgba(0, 0, 0, 0.38)" }}>
                                 Select Status
                              </span>
                            );

                          return renderStatusBadge(selected);
                        }}
                      >
                        {STATUS.map((status) => (
                          <MenuItem key={status.label} value={status.label}>
                            {renderStatusBadge(status.label)}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} sm={6} md={4} lg={3} xl={2}>
                    <MDBox
                      display="flex"
                      gap={1}
                      flexWrap="nowrap"
                      width="100%"
                    >
                      <MDButton
                        variant="contained"
                        color="deepBlue"
                        sx={{
                          flex: 1,
                          minWidth: "90px",
                          maxWidth: "160px",
                          whiteSpace: "nowrap",
                        }}
                        onClick={handleSearch}
                        disabled={listState.showLoaderOnClick}
                      >
                        {listState.showLoaderOnClick ? (
                          <i className="fa fa-spinner fa-spin fa-lg text-white" />
                        ) : (
                          <>
                            <Icon sx={{ mr: 1 }}>search</Icon>
                            Search
                          </>
                        )}
                      </MDButton>

                      {filters.filterParams.length > 0 && (
                        <MDButton
                          variant="contained"
                          color="deepBlue"
                          sx={{
                            flex: 1,
                            minWidth: "90px",
                            maxWidth: "160px",
                            whiteSpace: "nowrap",
                          }}
                          onClick={handleClear}
                        >
                          <Icon sx={{ mr: 1 }}>close</Icon>
                          Clear
                        </MDButton>
                      )}
                    </MDBox>
                  </Grid>
                </Grid>
              </MDBox>
              <MDBox
                px={3}
                py={1}
                display="flex"
                justifyContent="space-between"
                alignItems="center"
              >
                <MDTypography variant="body1" color="deepBlue" mb={1}>
                  Total Records: {contractorListData?.data?.totalRecords || 0}
                </MDTypography>
              </MDBox>
              <MDBox sx={{ width: "100%", overflowX: "auto" }}>
                {listState.isLoading ? (
                  <MDBox
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    height="150px"
                  >
                    <i className="fa fa-spinner fa-spin fa-2xl loader" />
                  </MDBox>
                ) : listState.error ? (
                  <MDBox
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    height="150px"
                  >
                    <MDTypography variant="body2" color="error">
                      {listState.error}
                    </MDTypography>
                  </MDBox>
                ) : listState.noRecords ? (
                  <MDBox
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    height="150px"
                  >
                    <MDTypography variant="body2" color="text">
                      No contractor found.
                    </MDTypography>
                  </MDBox>
                ) : (
                  <DataTable
                    columns={columns}
                    data={tableData || []}
                    isSorted={true}
                    pageNum={pageNum}
                    setPageNum={setPageNum}
                    totalPages={Math.ceil(
                      (contractorListData?.data?.totalRecords || 0) /
                        (contractorListData?.data?.noOfRecordsPerPage ||
                          DEFAULT_RECORDS_PER_PAGE),
                    )}
                  />
                )}
              </MDBox>
            </Card>
          </Grid>
        </Grid>

        <CenterPopup isOpen={panelMode !== null} onClose={closeModal}>
          <MDBox sx={{ p: 2 }}>
            <MDTypography variant="h6" mb={2}>
              {panelMode === "edit" ? "Edit Contractor" : "Add Contractor"}
            </MDTypography>

            {listState.isLoading ? (
              <MDBox
                display="flex"
                justifyContent="center"
                alignItems="center"
                height="150px"
              >
                <i className="fa fa-spinner fa-spin fa-2xl loader" />
              </MDBox>
            ) : (
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <RequiredLabel required>Name</RequiredLabel>
                  <MDInput
                    variant="outlined"
                    type="text"
                    name="name"
                    placeholder="Enter contractor name"
                    value={formData.name.value}
                    onChange={(e) =>
                      handleChange(e.target.name, e.target.value)
                    }
                    onBlur={(e) => handleOnBlur(e.target.name, e.target.value)}
                    error={!!formData.name.error}
                    fullWidth
                    inputProps={{ maxLength: 50 }}
                  />
                  {formData.name.error && (
                    <MDTypography variant="caption" color="error">
                      {formData.name.error}
                    </MDTypography>
                  )}
                </Grid>

                <Grid item xs={12}>
                  <RequiredLabel>Status</RequiredLabel>
                  <RadioGroup
                    row
                    name="status"
                    value={formData.status.value}
                    onChange={(e) =>
                      handleChange(e.target.name, e.target.value)
                    }
                  >
                    <FormControlLabel
                      value="ACTIVE"
                      control={<Radio size="small" />}
                      label="Active"
                      sx={radioLabelSx}
                    />
                    <FormControlLabel
                      value="INACTIVE"
                      control={<Radio size="small" />}
                      label="Inactive"
                      sx={radioLabelSx}
                    />
                  </RadioGroup>
                </Grid>

                {/* Save Button */}
                <Grid item xs={12}>
                  <MDBox display="flex" justifyContent="flex-end">
                    <MDButton
                      variant="gradient"
                      color="light"
                      onClick={closeModal}
                      sx={{ mr: 2 }}
                    >
                      Cancel
                    </MDButton>

                    <MDButton
                      type="submit"
                      variant="contained"
                      color="deepBlue"
                      onClick={handleSubmit}
                      disabled={listState.showLoaderOnClick}
                    >
                      {listState.showLoaderOnClick ? (
                        <i className="fa fa-spinner fa-spin" />
                      ) : (
                        "Save"
                      )}
                    </MDButton>
                  </MDBox>
                </Grid>
              </Grid>
            )}
          </MDBox>
        </CenterPopup>
      </MDBox>
    </DashboardLayout>
  );
}

export default ContractorListing;
